import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  myTeamsApi,
  type LineupWire,
  type MyTeamsReference,
  type MyTeamsSport,
} from '../api-client.js';

/**
 * The My Teams landing grid: one cell per (format x era) for the chosen
 * sport, each showing how full that lineup is and whether it is published.
 *
 * The grid is rendered from reference data rather than from saved
 * lineups, so every cell exists from the first visit — an empty cell is a
 * place to start, not an absence. Saved lineups are then merged in by
 * their cell key.
 */
export default function MyTeamsPage() {
  const [reference, setReference] = useState<MyTeamsReference | null>(null);
  const [lineups, setLineups] = useState<LineupWire[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sportSlug, setSportSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([myTeamsApi.reference(), myTeamsApi.listLineups()])
      .then(([ref, mine]) => {
        if (cancelled) return;
        setReference(ref);
        setLineups(mine);
        setSportSlug((current) => current ?? ref.sports[0]?.slug ?? null);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, []);

  const sport: MyTeamsSport | null = useMemo(
    () => reference?.sports.find((s) => s.slug === sportSlug) ?? null,
    [reference, sportSlug],
  );

  /** Saved lineups keyed by cell, so a cell lookup is O(1) per render. */
  const byCell = useMemo(() => {
    const map = new Map<string, LineupWire>();
    for (const lineup of lineups) {
      map.set(`${lineup.sport.slug}/${lineup.format.slug}/${lineup.era.slug}`, lineup);
    }
    return map;
  }, [lineups]);

  if (error) {
    return (
      <div className="ec-surface p-4 text-sm text-ec-face">
        <p className="font-display uppercase tracking-wide">My Teams unavailable</p>
        <p className="mt-2 text-ec-bone-dim">{error}</p>
      </div>
    );
  }

  if (!reference || !sport) {
    return <p className="p-4 text-sm text-ec-bone-dim">Loading My Teams…</p>;
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="font-display text-lg font-medium uppercase tracking-wide text-ec-face">
          My Teams
        </h1>
        <p className="text-xs text-ec-bone-dim">
          One lineup per format and era. Pick a cell to start building.
        </p>
      </header>

      <nav className="flex flex-wrap gap-2" aria-label="Sport">
        {reference.sports.map((option) => (
          <button
            key={option.slug}
            type="button"
            onClick={() => setSportSlug(option.slug)}
            aria-pressed={option.slug === sport.slug}
            className={
              option.slug === sport.slug
                ? 'ec-btn ec-btn--primary !py-1.5 text-sm'
                : 'ec-btn !py-1.5 text-sm'
            }
          >
            {option.name}
          </button>
        ))}
      </nav>

      {reference.lifetimeStartYear === null && (
        <p className="rounded-md border border-ec-brass/60 bg-[rgba(245,168,28,0.18)] p-3 text-sm">
          The <strong>Lifetime</strong> era is unavailable: this deployment has no birth year
          configured, so there is no cutoff to resolve it against. Current and All-time work
          normally.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sport.formats.flatMap((format) =>
          reference.eras.map((era) => {
            const lineup = byCell.get(`${sport.slug}/${format.slug}/${era.slug}`);
            const disabled = era.slug === 'lifetime' && reference.lifetimeStartYear === null;
            const filled = lineup?.completeness.filled ?? 0;

            const card = (
              <div className="ec-surface flex flex-col gap-2 p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-display text-sm uppercase tracking-wide text-ec-face">
                    {format.name}
                  </span>
                  <span className="shrink-0 rounded-sm border border-ec-brass/50 bg-ec-ground px-1.5 py-0.5 font-display text-[10px] uppercase tracking-wide text-ec-brass">
                    {era.name}
                  </span>
                </div>

                {lineup?.title && <p className="text-sm text-ec-face">{lineup.title}</p>}

                <p className="font-mono text-xs text-ec-bone-dim">
                  {filled} / {format.squadSize} picked
                </p>

                <div className="mt-1 flex items-center gap-2 text-xs">
                  {lineup?.isPublic ? (
                    <span className="text-ec-brass">● Published</span>
                  ) : (
                    <span className="text-ec-bone-dim">○ Private</span>
                  )}
                  {disabled && <span className="text-ec-bone-dim">— not configured</span>}
                </div>
              </div>
            );

            if (disabled) {
              return (
                <div key={`${format.slug}-${era.slug}`} className="opacity-50" aria-disabled>
                  {card}
                </div>
              );
            }

            return (
              <Link
                key={`${format.slug}-${era.slug}`}
                to={`/my-teams/${sport.slug}/${format.slug}/${era.slug}`}
                className="block transition-transform hover:-translate-y-0.5"
              >
                {card}
              </Link>
            );
          }),
        )}
      </div>
    </div>
  );
}
